import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { CsvDownloadButton } from '@/components/CsvDownloadButton';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function BulkUploadDialog({ open, onOpenChange, onSuccess }) {
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(API + '/catalogue/bulk-upload/template', {
        headers: { Authorization: 'Bearer ' + token },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'product_bulk_upload_template.xlsx';
      link.click();
      toast.success('Template downloaded');
    } catch (e) {
      toast.error('Failed to download template');
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) return toast.error('Select a file first');
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', uploadFile);
      const res = await axios.post(API + '/catalogue/bulk-upload', fd, {
        headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'multipart/form-data' }
      });
      setResult(res.data);
      const successful = res.data.successful_count || res.data.created_count || 0;
      const skipped = res.data.skipped_count || 0;
      const errors = res.data.error_count || 0;
      if (successful > 0) {
        toast.success(successful + ' product' + (successful === 1 ? '' : 's') + ' uploaded');
        if (onSuccess) onSuccess();
      }
      if (skipped > 0) toast.info(skipped + ' duplicate row' + (skipped === 1 ? '' : 's') + ' skipped');
      if (errors > 0) toast.warning(errors + ' validation error' + (errors === 1 ? '' : 's'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const downloadSkippedReport = () => {
    if (!result || !result.skipped_rows || result.skipped_rows.length === 0) return;
    const headers = ['Row', 'Product Name', 'Barcode', 'Reason', 'Existing Product ID'];
    const escape = (v) => '"' + String(v == null ? '' : v).replace(/"/g, '""') + '"';
    const rows = result.skipped_rows.map((r) => [r.row, r.product, r.barcode, r.reason, r.existing_product_id || ''].map(escape).join(','));
    const csv = [headers.map(escape).join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'bulk_upload_skipped_rows.csv';
    link.click();
  };

  const handleClose = () => {
    setUploadFile(null);
    setResult(null);
    onOpenChange(false);
  };

  return React.createElement(Dialog, { open, onOpenChange: handleClose },
    React.createElement(DialogContent, { className: "max-w-xl" },
      React.createElement(DialogHeader, null,
        React.createElement(DialogTitle, null, "Bulk Product Upload")
      ),
      React.createElement("div", { className: "py-4 space-y-4" },
        React.createElement("div", { className: "p-3 bg-blue-50 rounded border border-blue-200" },
          React.createElement("p", { className: "text-sm text-blue-800 mb-2" }, "Step 1: Download the Excel template"),
          React.createElement(Button, { variant: "outline", size: "sm", onClick: downloadTemplate, "data-testid": "bulk-download-template-btn" }, "Download Template"),
          React.createElement(CsvDownloadButton, { onDownload: downloadTemplate, testId: "bulk-download-template-csv", label: "Template CSV", className: "ml-2" })
        ),
        React.createElement("div", { className: "p-3 bg-slate-50 rounded border" },
          React.createElement("p", { className: "text-sm text-slate-700 mb-1" }, "Step 2: Fill product data in template"),
          React.createElement("p", { className: "text-xs text-slate-500" }, "Brand & Category must match Admin Master Data. Duplicate (Name + Barcode) rows are skipped automatically.")
        ),
        React.createElement("div", { className: "p-3 bg-green-50 rounded border border-green-200" },
          React.createElement("p", { className: "text-sm text-green-800 mb-2" }, "Step 3: Upload filled Excel file"),
          React.createElement("input", { type: "file", accept: ".xlsx,.xls,.csv", className: "hidden", ref: fileRef, onChange: (e) => setUploadFile(e.target.files[0]), "data-testid": "bulk-file-input" }),
          React.createElement("div", { className: "flex items-center gap-2" },
            React.createElement(Button, { variant: "outline", size: "sm", onClick: () => fileRef.current?.click(), "data-testid": "bulk-select-file-btn" }, "Select File"),
            uploadFile && React.createElement(Badge, { variant: "secondary" }, uploadFile.name)
          )
        ),
        result && React.createElement("div", { className: "p-3 rounded border", "data-testid": "bulk-result-block" },
          React.createElement("div", { className: "grid grid-cols-4 gap-2 text-center text-sm" },
            React.createElement("div", { className: "p-2 bg-slate-50 rounded", "data-testid": "bulk-total-tile" },
              React.createElement("p", { className: "font-bold text-slate-700" }, result.total_uploaded || result.total_processed || 0),
              React.createElement("p", { className: "text-xs text-slate-600" }, "Total Rows")
            ),
            React.createElement("div", { className: "p-2 bg-green-50 rounded", "data-testid": "bulk-successful-tile" },
              React.createElement("p", { className: "font-bold text-green-700" }, result.successful_count || result.created_count || 0),
              React.createElement("p", { className: "text-xs text-green-600" }, "Successful")
            ),
            React.createElement("div", { className: "p-2 bg-amber-50 rounded", "data-testid": "bulk-skipped-tile" },
              React.createElement("p", { className: "font-bold text-amber-700" }, result.skipped_count || 0),
              React.createElement("p", { className: "text-xs text-amber-600" }, "Skipped")
            ),
            React.createElement("div", { className: "p-2 bg-red-50 rounded", "data-testid": "bulk-errors-tile" },
              React.createElement("p", { className: "font-bold text-red-700" }, result.error_count || 0),
              React.createElement("p", { className: "text-xs text-red-600" }, "Errors")
            )
          ),
          result.skipped_rows && result.skipped_rows.length > 0 && React.createElement("div", { className: "mt-3 flex items-center justify-between" },
            React.createElement("p", { className: "text-xs text-amber-700" }, result.skipped_rows.length + " duplicate row" + (result.skipped_rows.length === 1 ? "" : "s") + " were skipped."),
            React.createElement(Button, { size: "sm", variant: "outline", onClick: downloadSkippedReport, className: "h-7 text-xs border-amber-300 text-amber-800 hover:bg-amber-50", "data-testid": "bulk-download-skipped-btn" }, "Download Skipped Report (CSV)")
          ),
          result.skipped_rows && result.skipped_rows.length > 0 && React.createElement("div", { className: "mt-2 max-h-32 overflow-y-auto text-xs space-y-0.5" },
            result.skipped_rows.slice(0, 8).map((r, i) =>
              React.createElement("p", { key: i, className: "text-amber-700" }, "Row " + r.row + ": " + r.product + " — " + r.reason)
            ),
            result.skipped_rows.length > 8 && React.createElement("p", { className: "text-slate-400 italic" }, "...and " + (result.skipped_rows.length - 8) + " more (download CSV for full list)")
          ),
          result.errors && result.errors.length > 0 && React.createElement("div", { className: "mt-3 max-h-32 overflow-y-auto text-xs" },
            result.errors.slice(0, 10).map((err, i) =>
              React.createElement("p", { key: i, className: "text-red-600" }, "Row " + err.row + ": " + err.errors.join(", "))
            )
          )
        )
      ),
      React.createElement(DialogFooter, null,
        React.createElement(Button, { variant: "outline", onClick: handleClose, "data-testid": "bulk-close-btn" }, "Close"),
        React.createElement(Button, { onClick: handleUpload, disabled: !uploadFile || uploading, "data-testid": "bulk-upload-btn" }, uploading ? "Uploading..." : "Upload")
      )
    )
  );
}

export default BulkUploadDialog;
